// frontend/src/pages/admin/Reports.jsx
// ============================================
// REPORTS - Clean design matching theme
// ============================================

import React, { useState, useEffect, useCallback } from "react";
import ConfirmationModal from "../../components/admin/ConfirmationModal";
import Modal from "../../components/admin/Modal";
import "./ManagementPages.css";
import * as adminApi from '../../services/admin';
import * as XLSX from "xlsx";
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const Reports = () => {
  const [reportType, setReportType] = useState("booking");
  const [reportData, setReportData] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reportStatusFilter, setReportStatusFilter] = useState("all");
  const [reportOasisFilter, setReportOasisFilter] = useState("all");
  const [reportSourceFilter, setReportSourceFilter] = useState("all");
  const [reportPaymentMethodFilter, setReportPaymentMethodFilter] = useState("all");
  const [dateSortDirection, setDateSortDirection] = useState(null); // 'asc' or 'desc' or null
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState("excel");
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    confirmText: 'Confirm',
    cancelText: 'Cancel'
  });
  const [selectedInspection, setSelectedInspection] = useState(null);

  // Set default dates on component mount
  useEffect(() => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    
    const startDateStr = firstDayOfMonth.getFullYear() + "-" +
                         String(firstDayOfMonth.getMonth() + 1).padStart(2, "0") + "-" +
                         String(firstDayOfMonth.getDate()).padStart(2, "0");
    
    const endDateStr = today.getFullYear() + "-" +
                       String(today.getMonth() + 1).padStart(2, "0") + "-" +
                       String(today.getDate()).padStart(2, "0");
    
    setStartDate(startDateStr);
    setEndDate(endDateStr);
  }, []);

  const showConfirmationModal = (title, message, onConfirm, confirmText = 'OK', cancelText = 'Cancel') => {
    setConfirmationModal({
      isOpen: true,
      title,
      message,
      onConfirm: () => {
        setConfirmationModal(prev => ({ ...prev, isOpen: false }));
        if (onConfirm) onConfirm();
      },
      confirmText,
      cancelText
    });
  };

  const closeInspectionReport = async (inspectionId) => {
    try {
      await adminApi.reviewInspectionReport(inspectionId);
      setReportData((current) => Array.isArray(current)
        ? current.filter((inspection) => inspection._id !== inspectionId)
        : current);
      setSelectedInspection((current) => current?._id === inspectionId ? null : current);
    } catch (error) {
      console.error('Error closing inspection report:', error);
      showConfirmationModal(
        'Error',
        error.response?.data?.error || 'Failed to close the inspection report.',
        null,
        'OK'
      );
    }
  };

  const confirmCloseInspectionReport = (inspection) => {
    setSelectedInspection(null);
    showConfirmationModal(
      'Close Inspection Report',
      'Mark this report as reviewed? It will be removed from the pending inspection report list.',
      () => closeInspectionReport(inspection._id),
      'Mark Reviewed',
      'Cancel'
    );
  };

  const downloadFile = (content, fileName, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const buildCsvContent = (rows) => {
    if (!rows || rows.length === 0) return "";

    const headers = Object.keys(rows[0]);
    const escapeCsvValue = (value) => {
      if (value === null || value === undefined) return "";
      const stringValue = String(value);
      return `"${stringValue.replace(/"/g, '""')}"`;
    };

    const lines = [headers.join(",")];
    rows.forEach((row) => {
      lines.push(headers.map((header) => escapeCsvValue(row[header])).join(","));
    });

    return lines.join("\r\n");
  };

  const getBookingPaymentMethod = (booking) => {
    const { paymentMethod, finalPaymentMethod } = booking;
    if (paymentMethod && finalPaymentMethod && paymentMethod !== finalPaymentMethod) {
      return `${paymentMethod} (downpayment), ${finalPaymentMethod} (final)`;
    }
    return finalPaymentMethod || paymentMethod || "N/A";
  };

  // Helper function to get the data array based on report type
  const getDataArray = useCallback(() => {
    if (!reportData) return [];
    
    if (reportType === "sales") {
      if (reportData.sales && Array.isArray(reportData.sales)) {
        return reportData.sales;
      }
      return [];
    }
    
    if (Array.isArray(reportData)) {
      return reportData;
    }
    
    console.error("getDataArray: Unexpected data structure", { reportType, reportData });
    return [];
  }, [reportData, reportType]);

  // Return data sorted according to current sort settings (only applies to booking report)
  const getSortedData = useCallback(() => {
    const data = getDataArray();
    if (reportType !== "booking" || !dateSortDirection) return data;

    const sorted = [...data].sort((a, b) => {
      const aDate = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bDate = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return dateSortDirection === "asc" ? aDate - bDate : bDate - aDate;
    });
    return sorted;
  }, [getDataArray, reportType, dateSortDirection]);

  const packageBookingCounts = reportType === "booking" && Array.isArray(reportData)
    ? Array.from(reportData.reduce((counts, booking) => {
        const oasis = booking.oasis || "Unknown Oasis";
        const packageName = booking.package || "Unspecified";
        const key = JSON.stringify([oasis, packageName]);
        const count = counts.get(key) || { oasis, packageName, bookings: 0 };
        count.bookings += 1;
        counts.set(key, count);
        return counts;
      }, new Map()).values()).sort((a, b) =>
        a.oasis.localeCompare(b.oasis) || a.packageName.localeCompare(b.packageName)
      )
    : [];

  // Debug logging
  useEffect(() => {
    console.log("Current state:", { reportType, reportData, dataArray: getDataArray() });
  }, [reportType, reportData, getDataArray]);

  const handleGenerateReport = async () => {
    try {
      setLoading(true);
      let data;

      let queryStartDate = startDate;
      let queryEndDate = endDate;

      console.log("Generating report:", reportType, "Dates:", queryStartDate, queryEndDate);

      if (reportType === "sales") {
        // Fetch sales using the reports endpoint first
        let result;
        try {
          result = await adminApi.getSalesReport(queryStartDate, queryEndDate);
          console.log("Sales Report API Response:", result);
        } catch (err) {
          console.error("Error from getSalesReport:", err);
          result = { sales: [], totalSales: 0 };
        }
        
        // Handle different response structures
        if (result && result.success) {
          data = { 
            sales: result.sales || result.data || [], 
            totalSales: result.totalSales || 0 
          };
        } else if (result && result.sales) {
          data = { 
            sales: result.sales, 
            totalSales: result.totalSales || 0 
          };
        } else if (Array.isArray(result)) {
          data = { 
            sales: result, 
            totalSales: result.reduce((sum, item) => sum + (item.amount || 0), 0)
          };
        } else {
          data = { sales: [], totalSales: 0 };
        }

        // If no data from reports endpoint, try fetching all sales and filter manually
        if (!data.sales || data.sales.length === 0) {
          console.log("No sales from reports endpoint, trying getAllSales...");
          try {
            const allSalesResult = await adminApi.getAllSales();
            const allSales = Array.isArray(allSalesResult) ? allSalesResult : (allSalesResult?.sales || []);
            
            if (allSales.length > 0 && queryStartDate && queryEndDate) {
              const start = new Date(queryStartDate);
              const end = new Date(queryEndDate);
              end.setHours(23, 59, 59, 999);
              
              const filteredSales = allSales.filter(sale => {
                const saleDate = new Date(sale.date);
                return saleDate >= start && saleDate <= end;
              });
              
              data = {
                sales: filteredSales,
                totalSales: filteredSales.reduce((sum, sale) => sum + (sale.amount || 0), 0)
              };
              console.log(`Filtered ${filteredSales.length} sales from date range`);
            }
          } catch (err) {
            console.error("Error from getAllSales fallback:", err);
          }
        }

        // Sort sales by booking number
        if (data.sales && Array.isArray(data.sales) && data.sales.length > 0) {
          data.sales.sort((a, b) => {
            const aNum = a.booking?.bookingNumber || a.bookingNumber || 0;
            const bNum = b.booking?.bookingNumber || b.bookingNumber || 0;
            return aNum - bNum;
          });
        }
        
        console.log("Processed sales data:", data);
        
      } else if (reportType === "booking") {
        // Fetch booking report
        let allBookings = await adminApi.getAllBookings();
        if (!allBookings || !Array.isArray(allBookings)) {
          allBookings = [];
        }

        // Filter by date range if provided (based on createdAt)
        if (queryStartDate && queryEndDate) {
          const start = new Date(queryStartDate);
          const end = new Date(queryEndDate);
          end.setHours(23, 59, 59, 999); // Include end date fully

          allBookings = allBookings.filter((booking) => {
            const createdDate = new Date(booking.createdAt);
            return createdDate >= start && createdDate <= end;
          });
        }

        const allowedStatuses = ["completed", "cancelled"];
        allBookings = allBookings.filter((booking) => {
          const status = (booking.status || "").toLowerCase();
          return allowedStatuses.includes(status) &&
            (reportStatusFilter === "all" || status === reportStatusFilter.toLowerCase());
        });

        if (reportOasisFilter !== "all") {
          allBookings = allBookings.filter((booking) => booking.oasis === reportOasisFilter);
        }

        if (reportSourceFilter === "unrecorded") {
          allBookings = allBookings.filter((booking) => !booking.bookingSource);
        } else if (reportSourceFilter !== "all") {
          allBookings = allBookings.filter((booking) => booking.bookingSource === reportSourceFilter);
        }

        if (reportPaymentMethodFilter !== "all") {
          const selectedMethod = reportPaymentMethodFilter.toLowerCase();
          allBookings = allBookings.filter((booking) =>
            [booking.paymentMethod, booking.finalPaymentMethod]
              .some((method) => method?.toLowerCase() === selectedMethod)
          );
        }

        // Sort by booking number
        allBookings.sort((a, b) => {
          const aNum = a.bookingNumber || 0;
          const bNum = b.bookingNumber || 0;
          return aNum - bNum;
        });

        data = allBookings;
        
      } else if (reportType === "inventory") {
        // Fetch inventory report
        data = await adminApi.getInventoryUsageReport(queryStartDate, queryEndDate);
        
        if (!data || !Array.isArray(data)) {
          data = [];
        }
      } else if (reportType === "inspection") {
        data = await adminApi.getInspectionReport(queryStartDate, queryEndDate);

        if (!data || !Array.isArray(data)) {
          data = [];
        }
      } else if (reportType === "pool-monitoring") {
        data = await adminApi.getPoolMonitoringReport(queryStartDate, queryEndDate);

        if (!data || !Array.isArray(data)) {
          data = [];
        }
      }

      setReportData(data);
      
      // Show message if no data
      if (reportType === "sales" && (!data.sales || data.sales.length === 0)) {
        showConfirmationModal("No Data", "No sales data found for the selected date range.", null, "OK");
      } else if (reportType !== "sales" && (!data || data.length === 0)) {
        showConfirmationModal("No Data", "No data found for the selected date range.", null, "OK");
      }
      
    } catch (error) {
      console.error("Error generating report:", error);
      showConfirmationModal("Error", `Error generating report: ${error.response?.data?.message || error.message || "Unknown error"}`, null, "OK");
      setReportData(reportType === "sales" ? { sales: [], totalSales: 0 } : []);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      if (!reportData) {
        showConfirmationModal("No Report", "Please generate a report first", null, "OK");
        return;
      }

      setExporting(true);

      let exportData = [];
      let sheetName = "";
      let filename = "";
      let formatExtension = "xlsx";

      if (exportFormat === "word") {
        formatExtension = "docx";
      } else if (exportFormat === "pdf") {
        formatExtension = "pdf";
      } else {
        formatExtension = "xlsx";
      }

      if (reportType === "sales") {
        const salesData = reportData.sales || [];
        
        if (salesData.length === 0) {
          showConfirmationModal("No Data", "No data to export", null, "OK");
          setExporting(false);
          return;
        }
        
        exportData = salesData.map((row) => ({
          "Reference Code": row.referenceCode || row.booking?.bookingReference || row.bookingReference || "N/A",
          "Location": row.location || row.booking?.oasis || "N/A",
          "Guest Name": row.booking?.customerName || row.customerName || row.guestName || "N/A",
          "Amount": toCurrencyNumber(row.amount ?? row.totalAmount),
          "Date": row.date ? new Date(row.date).toLocaleDateString() : "N/A",
        }));
        
        sheetName = "Sales Report";
        filename = `sales-report-${new Date().toISOString().split("T")[0]}.${formatExtension}`;
        
      } else if (reportType === "booking") {
        const bookingData = Array.isArray(reportData) ? reportData : [];
        
        if (bookingData.length === 0) {
          showConfirmationModal("No Data", "No data to export", null, "OK");
          setExporting(false);
          return;
        }
        
        exportData = bookingData.map((row) => ({
          "Booking Reference": row.bookingReference || "N/A",
          "Guest Name": row.customerName || "N/A",
          "Contact No.": row.customerContact || "N/A",
          "Pool/Villa Name": row.oasis || "N/A",
          "Booking Source": row.bookingSource === "Online" ? "Online Reservation" : row.bookingSource || "Not Recorded",
          "Amount": toCurrencyNumber(row.totalAmount),
          "Payment Status": row.paymentStatus || "Pending",
          "Payment Method": getBookingPaymentMethod(row),
          "Booking Date": row.createdAt ? new Date(row.createdAt).toLocaleDateString() : "N/A",
          "Reservation Date": row.bookingDate ? new Date(row.bookingDate).toLocaleDateString() : "N/A",
          "Time Slot": row.session || "N/A",
          "No. of Guests": row.pax || 0,
          "Total Paid": toCurrencyNumber(row.paymentType === "fullpayment" ? row.totalAmount : row.downpayment),
          "Balance": toCurrencyNumber(row.paymentType === "fullpayment" ? 0 : (row.totalAmount || 0) - (row.downpayment || 0)),
          "Status": row.status || "Pending",
        }));
        
        sheetName = "Booking Report";
        filename = `booking-report-${new Date().toISOString().split("T")[0]}.${formatExtension}`;
        
      } else if (reportType === "inventory") {
        const inventoryData = Array.isArray(reportData) ? reportData : [];
        
        if (inventoryData.length === 0) {
          showConfirmationModal("No Data", "No data to export", null, "OK");
          setExporting(false);
          return;
        }
        
        exportData = inventoryData.map((row) => ({
          "Item Name": row.item || row.name || "N/A",
          "Total Used": row.totalUsed || 0,
          "Current Stock": row.currentStock || 0,
        }));
        
        sheetName = "Inventory Report";
        filename = `inventory-report-${new Date().toISOString().split("T")[0]}.${formatExtension}`;
      } else if (reportType === "inspection") {
        const inspectionData = Array.isArray(reportData) ? reportData : [];

        if (inspectionData.length === 0) {
          showConfirmationModal("No Data", "No data to export", null, "OK");
          setExporting(false);
          return;
        }

        exportData = inspectionData.map((row) => ({
          "Date": row.date ? new Date(row.date).toLocaleDateString() : "N/A",
          "Room": row.roomName || "N/A",
          "Inspected By": row.inspectedBy || "N/A",
          "Condition": row.condition || "N/A",
          "Damage Found": row.damageFound || "No",
          "Items Needed": row.itemsNeeded || "N/A",
          "Status": row.status || "N/A",
          "Rating": row.rating || "N/A",
        }));

        sheetName = "Inspection Report";
        filename = `inspection-report-${new Date().toISOString().split("T")[0]}.${formatExtension}`;
      } else if (reportType === "pool-monitoring") {
        const poolData = Array.isArray(reportData) ? reportData : [];

        if (poolData.length === 0) {
          showConfirmationModal("No Data", "No data to export", null, "OK");
          setExporting(false);
          return;
        }

        exportData = poolData.map((row) => ({
          "Date": row.timestamp ? new Date(row.timestamp).toLocaleString() : "N/A",
          "Oasis": row.oasis || "N/A",
          "pH": row.ph ?? 0,
          "Temperature": row.temperature ?? 0,
          "Turbidity": row.turbidity || "N/A",
          "Status": row.status || "N/A",
        }));

        sheetName = "Pool Monitoring History";
        filename = `pool-monitoring-report-${new Date().toISOString().split("T")[0]}.${formatExtension}`;
      }

      const moneyColumns = reportType === "booking"
        ? ["Amount", "Total Paid", "Balance"]
        : reportType === "sales" ? ["Amount"] : [];
      const formattedExportData = exportData.map((row) =>
        Object.fromEntries(Object.entries(row).map(([key, value]) => [
          key,
          moneyColumns.includes(key) ? formatCurrency(value) : value,
        ]))
      );

      if (exportFormat === "word") {
        // Word export: create an HTML-based .doc file which Word can open
        const htmlHeader = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${sheetName}</title><style>body{font-family: Arial, Helvetica, sans-serif; color:#111;} table{border-collapse:collapse;width:100%} th,td{border:1px solid #ddd;padding:8px;} th{background:#0284c7;color:#fff;text-align:left}</style></head><body>`;
        const htmlFooter = `</body></html>`;
        let tableHtml = `<h2 style="color:#0284c7">${sheetName}</h2>`;
        tableHtml += `<table><thead><tr>`;
        const cols = Object.keys(formattedExportData[0] || {});
        cols.forEach(c => { tableHtml += `<th>${c}</th>`; });
        tableHtml += `</tr></thead><tbody>`;
        formattedExportData.forEach(row => {
          tableHtml += `<tr>`;
          cols.forEach(c => { tableHtml += `<td>${String(row[c] ?? '')}</td>`; });
          tableHtml += `</tr>`;
        });
        tableHtml += `</tbody></table>`;
        const wordContent = `\uFEFF${htmlHeader}${tableHtml}${htmlFooter}`;
        const blob = new Blob([wordContent], { type: 'application/msword;charset=utf-8' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename.replace(/\.docx?$|\.pdf$|\.xlsx?$/i, '.doc');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else if (exportFormat === "pdf") {
        // Use jsPDF + autoTable to create a professional PDF
        try {
          const fontResponse = await fetch(`${process.env.PUBLIC_URL}/fonts/NotoSans.ttf`);
          if (!fontResponse.ok) {
            throw new Error("Unable to load the PDF font.");
          }

          const fontBytes = new Uint8Array(await fontResponse.arrayBuffer());
          let fontBinary = "";
          for (let index = 0; index < fontBytes.length; index += 0x8000) {
            fontBinary += String.fromCharCode(...fontBytes.subarray(index, index + 0x8000));
          }

          const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
          doc.addFileToVFS("NotoSans.ttf", btoa(fontBinary));
          doc.addFont("NotoSans.ttf", "NotoSans", "normal");
          doc.setFont("NotoSans", "normal");
          const margin = 40;
          doc.setFontSize(16);
          doc.setTextColor('#0284c7');
          doc.text(sheetName, margin, 50);

          // Subtitle / date range
          const rangeText = (startDate && endDate) ? `${new Date(startDate).toLocaleDateString()} - ${new Date(endDate).toLocaleDateString()}` : '';
          doc.setFontSize(10);
          doc.setTextColor('#444');
          if (rangeText) doc.text(rangeText, margin, 68);

          const cols = Object.keys(formattedExportData[0] || {});
          const rows = formattedExportData.map(r => cols.map(c =>
            (r[c] === null || r[c] === undefined) ? '' : String(r[c])
          ));

          doc.autoTable({
            head: [cols],
            body: rows,
            startY: 80,
            theme: 'striped',
            headStyles: { font: 'NotoSans', fillColor: [2,132,199], textColor: 255, halign: 'left' },
            styles: { font: 'NotoSans', fontSize: 9, cellPadding: 6, overflow: 'linebreak' },
            columnStyles: cols.reduce((acc, c, i) => { acc[i] = { cellWidth: 'auto' }; return acc; }, {}),
            didDrawPage: (data) => {
              const pageCount = doc.internal.getNumberOfPages();
              const pageSize = doc.internal.pageSize;
              doc.setFont("NotoSans", "normal");
              doc.setFontSize(9);
              doc.setTextColor('#888');
              const footerText = `Generated: ${new Date().toLocaleString()} — Page ${doc.internal.getCurrentPageInfo().pageNumber} of ${pageCount}`;
              doc.text(footerText, margin, pageSize.height - 30);
            }
          });

          doc.save(filename);
        } catch (err) {
          console.error('Error generating PDF with jsPDF:', err);
          showConfirmationModal('Error', 'Failed to generate PDF: ' + err.message, null, 'OK');
        }
      } else {
        // Create worksheet and workbook
        const ws = XLSX.utils.json_to_sheet(formattedExportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, sheetName);

        // Auto-size columns and center-align content
        const maxWidth = 50;
        const colWidths = Object.keys(exportData[0] || {}).map((key) => ({
          wch: Math.min(Math.max(key.length, 15), maxWidth),
        }));
        ws["!cols"] = colWidths;

        const range = XLSX.utils.decode_range(ws["!ref"] || "A1");
        for (let row = range.s.r; row <= range.e.r; row += 1) {
          for (let col = range.s.c; col <= range.e.c; col += 1) {
            const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
            const cell = ws[cellRef];
            if (cell) {
              cell.s = cell.s || {};
              cell.s.alignment = { horizontal: "center", vertical: "center" };
              const headerRef = XLSX.utils.encode_cell({ r: range.s.r, c: col });
              if (row === range.s.r) {
                cell.s.font = { ...cell.s.font, bold: true };
              }
            }
          }
        }

        XLSX.writeFile(wb, filename);
      }
      
    } catch (error) {
      console.error("Error exporting Excel:", error);
      showConfirmationModal("Error", "Error exporting report: " + error.message, null, "OK");
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-PH", {
      style: "currency",
      currency: "PHP",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(toCurrencyNumber(amount));
  };

  const toCurrencyNumber = (amount) => {
    const value = Number(amount ?? 0);
    return Number.isFinite(value) ? value : 0;
  };

  // Helper function to check if data is empty
  const isEmptyData = () => {
    const dataArray = getDataArray();
    return dataArray.length === 0;
  };

  // Helper function to get colSpan for no data message
  const getNoDataColSpan = () => {
    if (reportType === "booking") return "15";
    if (reportType === "sales") return "5";
    if (reportType === "inspection") return "9";
    if (reportType === "pool-monitoring") return "6";
    return "3";
  };

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Reports</h1>
      </div>

      {/* Filter Section */}
      <div className="filters-card">
        <div className="filters-grid">
          <div className="filter-group">
            <label>Report Type</label>
            <select
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              className="filter-select"
            >
              <option value="booking">Booking Report</option>
              <option value="sales">Sales Report</option>
              <option value="inventory">Inventory Usage Report</option>
              <option value="inspection">Inspection Report</option>
              <option value="pool-monitoring">Pool Monitoring History</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="filter-input"
            />
          </div>
          
          <div className="filter-group">
            <label>End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="filter-input"
            />
          </div>

          {reportType === "booking" && (
            <>
              <div className="filter-group">
                <label>Status</label>
                <select
                  value={reportStatusFilter}
                  onChange={(e) => setReportStatusFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All Statuses</option>
                  <option value="Completed">Completed</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>
              <div className="filter-group">
                <label>Oasis</label>
                <select
                  value={reportOasisFilter}
                  onChange={(e) => setReportOasisFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All Oases</option>
                  <option value="Oasis 1">Oasis 1</option>
                  <option value="Oasis 2">Oasis 2</option>
                </select>
              </div>
              <div className="filter-group">
                <label>Booking Source</label>
                <select
                  value={reportSourceFilter}
                  onChange={(e) => setReportSourceFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All Sources</option>
                  <option value="Online">Online Reservation</option>
                  <option value="Walk-in">Walk-in</option>
                  <option value="unrecorded">Source Not Recorded</option>
                </select>
              </div>
              <div className="filter-group">
                <label>Payment Method</label>
                <select
                  value={reportPaymentMethodFilter}
                  onChange={(e) => setReportPaymentMethodFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All Payment Methods</option>
                  <option value="Cash">Cash</option>
                  <option value="GCash">GCash</option>
                  <option value="Maya">Maya</option>
                  <option value="GoTyme">GoTyme</option>
                  <option value="SeaBank">SeaBank</option>
                </select>
              </div>
            </>
          )}

          <div className="filter-actions">
            <button
              className="btn-primary"
              onClick={handleGenerateReport}
              disabled={loading}
            >
              {loading ? "Generating..." : "Generate Report"}
            </button>
            <select
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value)}
              className="filter-select"
            >
              <option value="excel">Excel (.xlsx)</option>
              <option value="pdf">PDF (.pdf)</option>
              <option value="word">Word (.docx)</option>
            </select>
            <button
              className="btn-outline"
              onClick={handleExport}
              disabled={!reportData || exporting || loading}
            >
              {exporting ? "Exporting..." : "Export Report"}
            </button>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Generating report...</p>
        </div>
      )}

      {/* Report Results */}
      {reportData && !loading && (
        <div className="report-container">
          <div className="report-header">
            <h3>
              {reportType.charAt(0).toUpperCase() + reportType.slice(1)} Report
            </h3>
            {startDate && endDate && (
              <p className="report-date-range">
                {new Date(startDate).toLocaleDateString()} -{" "}
                {new Date(endDate).toLocaleDateString()}
              </p>
            )}
          </div>

          {/* Summary for Sales Report */}
          {reportType === "sales" && reportData.totalSales !== undefined && (
            <div className="stats-grid small">
              <div className="stat-card">
                <div className="stat-title">Total Sales</div>
                <div className="stat-value">
                  {formatCurrency(reportData.totalSales)}
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-title">Transactions</div>
                <div className="stat-value">
                  {getDataArray().length}
                </div>
              </div>
            </div>
          )}

          {reportType === "inspection" && (
            <div className="stats-grid small">
              <div className="stat-card">
                <div className="stat-title">Inspection Records</div>
                <div className="stat-value">{getDataArray().length}</div>
              </div>
            </div>
          )}

          {reportType === "pool-monitoring" && (
            <div className="stats-grid small">
              <div className="stat-card">
                <div className="stat-title">Monitoring Entries</div>
                <div className="stat-value">{getDataArray().length}</div>
              </div>
            </div>
          )}

          {reportType === "booking" && (
            <div className="package-booking-summary">
              <h4>Bookings by Package</h4>
              <p>Counts are based on the bookings included in this report.</p>
              <div className="table-responsive">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Oasis</th>
                      <th>Package</th>
                      <th>Bookings</th>
                    </tr>
                  </thead>
                  <tbody>
                    {packageBookingCounts.length > 0 ? (
                      packageBookingCounts.map(({ oasis, packageName, bookings }) => (
                        <tr key={`${oasis}-${packageName}`}>
                          <td>{oasis}</td>
                          <td>{packageName}</td>
                          <td>{bookings}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={3} className="no-data">
                          No bookings found for the selected filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Data Table */}
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  {reportType === "booking" && (
                    <>
                      <th style={{ color: '#000000', backgroundColor: '#f3f4f6' }}>Booking Reference</th>
                      <th style={{ color: '#000000', backgroundColor: '#f3f4f6' }}>Guest Name</th>
                      <th style={{ color: '#000000', backgroundColor: '#f3f4f6' }}>Contact No.</th>
                      <th style={{ color: '#000000', backgroundColor: '#f3f4f6' }}>Pool/Villa Name</th>
                      <th style={{ color: '#000000', backgroundColor: '#f3f4f6' }}>Booking Source</th>
                      <th style={{ color: '#000000', backgroundColor: '#f3f4f6' }}>Amount</th>
                      <th style={{ color: '#000000', backgroundColor: '#f3f4f6' }}>Payment Status</th>
                      <th style={{ color: '#000000', backgroundColor: '#f3f4f6' }}>Payment Method</th>
                      <th style={{ color: '#000000', backgroundColor: '#f3f4f6' }}>
                        Booking Date
                        <button
                          className="sort-button"
                          onClick={() => setDateSortDirection((d) => (d === "asc" ? "desc" : "asc"))}
                          title="Toggle sort by booking date"
                        >
                          {dateSortDirection === "asc" ? "▲" : "▼"}
                        </button>
                      </th>
                      <th style={{ color: '#000000', backgroundColor: '#f3f4f6' }}>Reservation Date</th>
                      <th style={{ color: '#000000', backgroundColor: '#f3f4f6' }}>Time Slot</th>
                      <th style={{ color: '#000000', backgroundColor: '#f3f4f6' }}>No. of Guests</th>
                      <th style={{ color: '#000000', backgroundColor: '#f3f4f6' }}>Total Paid</th>
                      <th style={{ color: '#000000', backgroundColor: '#f3f4f6' }}>Balance</th>
                      <th style={{ color: '#000000', backgroundColor: '#f3f4f6' }}>Status</th>
                    </>
                  )}
                  {reportType === "sales" && (
                    <>
                      <th style={{ color: '#000000', backgroundColor: '#f3f4f6' }}>Reference Code</th>
                      <th style={{ color: '#000000', backgroundColor: '#f3f4f6' }}>Location</th>
                      <th style={{ color: '#000000', backgroundColor: '#f3f4f6' }}>Guest Name</th>
                      <th style={{ color: '#000000', backgroundColor: '#f3f4f6' }}>Amount</th>
                      <th style={{ color: '#000000', backgroundColor: '#f3f4f6' }}>Date</th>
                    </>
                  )}
                  {reportType === "inventory" && (
                    <>
                      <th style={{ color: '#000000', backgroundColor: '#f3f4f6' }}>Item Name</th>
                      <th style={{ color: '#000000', backgroundColor: '#f3f4f6' }}>Total Used</th>
                      <th style={{ color: '#000000', backgroundColor: '#f3f4f6' }}>Current Stock</th>
                    </>
                  )}
                  {reportType === "inspection" && (
                    <>
                      <th style={{ color: '#000000', backgroundColor: '#f3f4f6' }}>Date</th>
                      <th style={{ color: '#000000', backgroundColor: '#f3f4f6' }}>Room</th>
                      <th style={{ color: '#000000', backgroundColor: '#f3f4f6' }}>Inspected By</th>
                      <th style={{ color: '#000000', backgroundColor: '#f3f4f6' }}>Condition</th>
                      <th style={{ color: '#000000', backgroundColor: '#f3f4f6' }}>Damage Found</th>
                      <th style={{ color: '#000000', backgroundColor: '#f3f4f6' }}>Items Needed</th>
                      <th style={{ color: '#000000', backgroundColor: '#f3f4f6' }}>Status</th>
                      <th style={{ color: '#000000', backgroundColor: '#f3f4f6' }}>Rating</th>
                      <th style={{ color: '#000000', backgroundColor: '#f3f4f6' }}>Actions</th>
                    </>
                  )}
                  {reportType === "pool-monitoring" && (
                    <>
                      <th style={{ color: '#000000', backgroundColor: '#f3f4f6' }}>Date</th>
                      <th style={{ color: '#000000', backgroundColor: '#f3f4f6' }}>Oasis</th>
                      <th style={{ color: '#000000', backgroundColor: '#f3f4f6' }}>pH</th>
                      <th style={{ color: '#000000', backgroundColor: '#f3f4f6' }}>Temperature</th>
                      <th style={{ color: '#000000', backgroundColor: '#f3f4f6' }}>Turbidity</th>
                      <th style={{ color: '#000000', backgroundColor: '#f3f4f6' }}>Status</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                  {isEmptyData() ? (
                  <tr>
                    <td colSpan={getNoDataColSpan()} className="no-data">
                      {reportType === "sales" 
                        ? "No sales data available for the selected date range"
                        : "No data available for the selected date range"}
                    </td>
                  </tr>
                ) : (
                  getSortedData().map((row, idx) => {
                    return (
                      <tr key={idx}>
                        {reportType === "booking" && (
                          <>
                            <td><strong>{row.bookingReference || "N/A"}</strong></td>
                            <td>{row.customerName || "N/A"}</td>
                            <td>{row.customerContact || "N/A"}</td>
                            <td>{row.oasis || "N/A"}</td>
                            <td>{row.bookingSource === "Online" ? "Online Reservation" : row.bookingSource || "Not Recorded"}</td>
                            <td className="amount">{formatCurrency(row.totalAmount || 0)}</td>
                            <td>
                              <span className={`status-badge status-${(row.paymentStatus || "pending").toLowerCase()}`}>
                                {row.paymentStatus || "Pending"}
                              </span>
                            </td>
                            <td>{getBookingPaymentMethod(row)}</td>
                            <td>{row.createdAt ? new Date(row.createdAt).toLocaleDateString() : "N/A"}</td>
                            <td>{row.bookingDate ? new Date(row.bookingDate).toLocaleDateString() : "N/A"}</td>
                            <td>{row.session || "N/A"}</td>
                            <td>{row.pax || 0}</td>
                            <td className="amount">
                              {formatCurrency(
                                row.paymentType === "fullpayment"
                                  ? row.totalAmount || 0
                                  : row.downpayment || 0
                              )}
                            </td>
                            <td className="amount">
                              {formatCurrency(
                                row.paymentType === "fullpayment"
                                  ? 0
                                  : (row.totalAmount || 0) - (row.downpayment || 0)
                              )}
                            </td>
                            <td>
                              <span className={`status-badge status-${(row.status || "pending").toLowerCase()}`}>
                                {row.status || "Pending"}
                              </span>
                            </td>
                          </>
                        )}
                        {reportType === "sales" && (
                          <>
                            <td className="reference-code"><strong>{row.referenceCode || row.booking?.bookingReference || row.bookingReference || "N/A"}</strong></td>
                            <td className="location">{row.location || row.booking?.oasis || "N/A"}</td>
                            <td>{row.booking?.customerName || row.customerName || row.guestName || "N/A"}</td>
                            <td className="amount">{formatCurrency(row.amount || row.totalAmount || 0)}</td>
                            <td>{row.date ? new Date(row.date).toLocaleDateString() : "N/A"}</td>
                          </>
                        )}
                        {reportType === "inventory" && (
                          <>
                            <td>{row.item || row.name || "N/A"}</td>
                            <td>{row.totalUsed || 0}</td>
                            <td>{row.currentStock || 0}</td>
                          </>
                        )}
                        {reportType === "inspection" && (
                          <>
                            <td>{row.date ? new Date(row.date).toLocaleDateString() : "N/A"}</td>
                            <td>{row.roomName || "N/A"}</td>
                            <td>{row.inspectedBy || "N/A"}</td>
                            <td>{row.condition || "N/A"}</td>
                            <td>{row.damageFound || "No"}</td>
                            <td>{row.itemsNeeded || "N/A"}</td>
                            <td>{row.status || "N/A"}</td>
                            <td>{row.rating || "N/A"}</td>
                            <td className="inspection-report-actions">
                              <button
                                className="btn-secondary btn-small"
                                onClick={() => setSelectedInspection(row)}
                              >
                                View Details
                              </button>
                              <button
                                className="btn-primary btn-small"
                                onClick={() => confirmCloseInspectionReport(row)}
                              >
                                Close Report
                              </button>
                            </td>
                          </>
                        )}
                        {reportType === "pool-monitoring" && (
                          <>
                            <td>{row.timestamp ? new Date(row.timestamp).toLocaleString() : "N/A"}</td>
                            <td>{row.oasis || "N/A"}</td>
                            <td>{row.ph ?? 0}</td>
                            <td>{row.temperature ?? 0}</td>
                            <td>{row.turbidity || "N/A"}</td>
                            <td>{row.status || "N/A"}</td>
                          </>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {confirmationModal.isOpen && (
        <ConfirmationModal
          title={confirmationModal.title}
          message={confirmationModal.message}
          onConfirm={confirmationModal.onConfirm}
          onCancel={() => setConfirmationModal(prev => ({ ...prev, isOpen: false }))}
          confirmText={confirmationModal.confirmText}
          cancelText={confirmationModal.cancelText}
        />
      )}

      {selectedInspection && (
        <Modal onClose={() => setSelectedInspection(null)}>
          <div className="modal-header">
            <h2>Inspection Report Details</h2>
            <button className="modal-close" onClick={() => setSelectedInspection(null)}>×</button>
          </div>
          <div className="modal-body inspection-report-details">
            {selectedInspection.proofImages?.length > 0 ? (
              selectedInspection.proofImages.map((image, index) => (
                <a key={`${image}-${index}`} href={image} target="_blank" rel="noreferrer">
                <img
                  src={image}
                  alt={`Inspection proof ${index + 1}`}
                  className="inspection-proof-image"
                  style={{ maxWidth: '100%', maxHeight: '420px', objectFit: 'contain', display: 'block', margin: '0 auto 20px' }}
                />
              </a>
              ))
            ) : selectedInspection.proofImage ? (
              <a href={selectedInspection.proofImage} target="_blank" rel="noreferrer">
              <img
                src={selectedInspection.proofImage}
                alt="Inspection proof"
                className="inspection-proof-image"
                style={{ maxWidth: '100%', maxHeight: '420px', objectFit: 'contain', display: 'block', margin: '0 auto 20px' }}
              />
              </a>
            ) : (
              <p>No proof image was submitted.</p>
            )}
            <dl>
              <dt>Room</dt><dd>{selectedInspection.roomName || 'N/A'}</dd>
              <dt>Inspected by</dt><dd>{selectedInspection.inspectedBy || 'N/A'}</dd>
              <dt>Date</dt><dd>{selectedInspection.date ? new Date(selectedInspection.date).toLocaleString() : 'N/A'}</dd>
              <dt>Cleanliness</dt><dd>{selectedInspection.cleanliness || selectedInspection.condition || 'N/A'}</dd>
              <dt>Furniture condition</dt><dd>{selectedInspection.furnitureCondition || 'N/A'}</dd>
              <dt>Cleaning needed</dt><dd>{selectedInspection.cleaningNeeded || 'N/A'}</dd>
              <dt>Electricity</dt><dd>{selectedInspection.electricityStatus || 'N/A'}</dd>
              <dt>Plumbing</dt><dd>{selectedInspection.plumbingStatus || 'N/A'}</dd>
              <dt>Damage found</dt><dd>{selectedInspection.damageFound || 'No'}</dd>
              <dt>Damage description</dt><dd>{selectedInspection.damageDescription || 'None'}</dd>
              <dt>Maintenance required</dt><dd>{selectedInspection.maintenanceRequired ? 'Yes' : 'No'}</dd>
              <dt>Maintenance notes</dt><dd>{selectedInspection.maintenanceNotes || 'None'}</dd>
              <dt>Items needed</dt><dd>{selectedInspection.itemsNeeded || 'None'}</dd>
              <dt>Rating</dt><dd>{selectedInspection.rating || 'N/A'}</dd>
              <dt>Additional notes</dt><dd>{selectedInspection.notes || 'None'}</dd>
            </dl>
          </div>
          <div className="modal-footer">
            <button className="btn-cancel-modal" onClick={() => setSelectedInspection(null)}>Close</button>
            <button className="btn-submit" onClick={() => confirmCloseInspectionReport(selectedInspection)}>Mark Reviewed</button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Reports;